<!-- Loading state -->
<div class="receipt-loading" *ngIf="isLoading" role="status" aria-live="polite" aria-label="Loading receipt, please wait">
  <mat-spinner diameter="40"></mat-spinner>
  <p class="loading-text">Loading receipt&hellip;</p>
</div>

<!-- Error state -->
<div class="receipt-error" *ngIf="!isLoading && loadError" role="alert">
  <mat-icon aria-hidden="true">error_outline</mat-icon>
  <p>{{ loadError }}</p>
  <button mat-stroked-button color="primary" (click)="loadReceiptData()">Retry</button>
</div>

<div class="receipt-page-container" *ngIf="receipt && !isLoading">

  <!-- Two-column layout: Success message + Receipt preview -->
  <div class="receipt-layout">

    <!-- Left Section: Success Message -->
    <div class="success-section" role="status" aria-live="polite">
      <div class="success-content">
        <div class="success-icon-wrapper">
          <mat-icon class="success-icon" aria-hidden="true">check_circle</mat-icon>
        </div>

        <h1 class="success-heading">PAYMENT PROCESSED SUCCESSFULLY!</h1>

        <p class="success-body">
          Your payment has been processed successfully. Please allow 3-5 business days
          for the transaction to be completed.
        </p>

        <p class="success-body">
          You will receive a confirmation email with the payment details shortly.
        </p>

        <p class="success-thank-you">Thank you for your payment!</p>

        <button mat-stroked-button class="go-to-payments-btn" (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          BACK
        </button>
        <button mat-stroked-button color="primary" class="go-to-payments-btn" (click)="goToPayments()">
          GO TO PAYMENTS
        </button>
      </div>
    </div>

    <!-- Right Section: Receipt Preview Card -->
    <div class="receipt-preview-section">
      <mat-card class="receipt-card" id="receipt-print-area">

        <!-- Receipt Header -->
        <div class="receipt-header">
          <div class="receipt-logo">
            <mat-icon class="logo-icon" aria-hidden="true">account_balance</mat-icon>
            <div class="logo-text">
              <span class="logo-title">{{ receipt.taxCollectorName || 'Tax Collector' }}</span>
              <span class="logo-subtitle" *ngIf="receipt.officeAddress">{{ receipt.officeAddress }}</span>
            </div>
          </div>
          <div class="receipt-title-block">
            <h2 class="receipt-title">Tourist Development Tax Receipt</h2>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- Customer Info Bar -->
        <div class="customer-info-bar">
          <span class="customer-label">Payer:</span>
          <span class="customer-name">{{ receipt.payerName || receipt.accountHolderName }}</span>
        </div>

        <!-- Receipt Metadata -->
        <div class="receipt-meta">
          <div class="meta-row">
            <div class="meta-item">
              <span class="meta-label">Receipt Number</span>
              <span class="meta-value">{{ receipt.receiptNumber }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Receipt Date</span>
              <span class="meta-value">{{ receipt.receiptDate | date:'MM/dd/yyyy' }}</span>
            </div>
          </div>
          <div class="meta-row">
            <div class="meta-item">
              <span class="meta-label">Set Number</span>
              <span class="meta-value">{{ receipt.setNumber }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Customer ID</span>
              <span class="meta-value">{{ receipt.customerId }}</span>
            </div>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- Return Info -->
        <div class="return-info-row">
          <span class="return-period">{{ receipt.period }} {{ receipt.returnType }}</span>
          <span class="return-status-badge paid">
            <mat-icon class="badge-icon" aria-hidden="true">check_circle</mat-icon>
            {{ receipt.returnStatus }}
          </span>
        </div>

        <mat-divider></mat-divider>

        <!-- Line Items Table -->
        <div class="line-items-section">
          <table class="line-items-table" aria-label="Payment line items">
            <tbody>
              <tr class="line-item">
                <td class="line-label">Taxable Rental Receipts</td>
                <td class="line-value">{{ receipt.taxableRentalReceipts | currency }}</td>
              </tr>
              <tr class="line-item">
                <td class="line-label">Exemptions</td>
                <td class="line-value">{{ receipt.totalExemptions | currency }}</td>
              </tr>
              <tr class="line-item">
                <td class="line-label">Total Taxes</td>
                <td class="line-value">{{ receipt.totalTaxes | currency }}</td>
              </tr>
              <tr class="line-item">
                <td class="line-label">Late Fee</td>
                <td class="line-value">{{ (receipt.lateFee ?? receipt.totalFees) | currency }}</td>
              </tr>
              <tr class="line-item">
                <td class="line-label">Late Interest</td>
                <td class="line-value">{{ (receipt.lateInterest ?? 0) | currency }}</td>
              </tr>
              <tr class="line-item">
                <td class="line-label">Not Sufficient Funds Fee</td>
                <td class="line-value">{{ (receipt.nsfFee ?? 0) | currency }}</td>
              </tr>
              <tr class="line-item" *ngIf="otherFees > 0">
                <td class="line-label">Other Fees</td>
                <td class="line-value">{{ otherFees | currency }}</td>
              </tr>
              <tr class="line-item allowance-row">
                <td class="line-label">Online Collection Allowance</td>
                <td class="line-value">- {{ receipt.totalAllowances | currency }}</td>
              </tr>
              <tr class="line-item total-amount-due-row">
                <td class="line-label"><strong>Total Amount Due</strong></td>
                <td class="line-value"><strong>{{ receipt.totalAmountDue | currency }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <mat-divider></mat-divider>

        <!-- Payment Methods Section -->
        <div class="payment-methods-section">
          <div class="payment-methods-header">
            <span class="payment-methods-title">Payment Methods</span>
            <span class="payment-type-badge">| {{ getPaymentTypeLabel() }}</span>
          </div>

          <ng-container *ngFor="let method of receipt.paymentMethods">
            <div class="payment-method-row">
              <span class="payment-method-label">{{ getPaymentMethodLabel(method) }}</span>
              <span class="payment-method-amount">{{ method.amount | currency }}</span>
            </div>
            <div class="payment-method-row fee-row" *ngIf="method.cardFeeAmount && method.cardFeeAmount > 0">
              <span class="payment-method-label fee-label">
                <ng-container *ngIf="method.cardFeePercentage; else processorFeeLabel">
                  Card Fee {{ method.cardFeePercentage }}%
                </ng-container>
                <ng-template #processorFeeLabel>Card processing fee (retained by payment processor)</ng-template>
              </span>
              <span class="payment-method-amount">{{ method.cardFeeAmount | currency }}</span>
            </div>
          </ng-container>
        </div>

        <mat-divider></mat-divider>

        <!-- Totals -->
        <div class="receipt-totals" aria-label="Receipt totals">
          <div class="totals-row total-paid-row">
            <span class="totals-label"><strong>Total Charged</strong></span>
            <span class="totals-value"><strong>{{ getTotalPaid() | currency }}</strong></span>
          </div>
          <!-- Only differs from Total Charged when a gateway card fee is present — the county
               never collected that fee, so the reconciliation figure needs its own line. -->
          <div class="totals-row" *ngIf="hasProcessorFee()">
            <span class="totals-label">Total Paid (county)</span>
            <span class="totals-value">{{ receipt?.totalPayment | currency }}</span>
          </div>
          <!-- Cash change handed back on overpayment (not a drawer over/short) -->
          @if (getCashChangeDue() > 0) {
            <div
              class="totals-row over-row"
              role="status"
              [attr.aria-label]="'Cash change due ' + (getCashChangeDue() | currency)"
            >
              <span class="totals-label over-short-label">
                <mat-icon class="over-short-icon" aria-hidden="true">payments</mat-icon>
                <strong>Cash Change Due</strong>
              </span>
              <span class="totals-value">
                <strong>{{ getCashChangeDue() | currency }}</strong>
              </span>
            </div>
          }
        </div>

        <mat-divider></mat-divider>

        <!-- Properties Section -->
        <div class="properties-section">
          <div class="properties-header">Properties</div>
          <ul class="properties-list" aria-label="Associated properties">
            <li *ngFor="let property of receipt.properties" class="property-item">
              {{ property.address }}
            </li>
          </ul>
        </div>

        <mat-divider></mat-divider>

        <!-- Receipt Footer -->
        <div class="receipt-footer">
          <span class="footer-office">{{ receipt.officeName }}</span>
          <span class="footer-separator" aria-hidden="true">|</span>
          <span class="footer-cashier">Cashier: {{ getCashierInitials() }}</span>
          <span class="footer-separator" aria-hidden="true">|</span>
          <span class="footer-date">Printed: {{ getPrintedDate() }}</span>
        </div>

      </mat-card>

      <!-- Print Button (below card, outside print area) -->
      <div class="print-button-wrapper no-print">
        <button mat-raised-button color="primary" class="print-receipt-btn" (click)="printReceipt()" aria-label="Print receipt">
          <mat-icon aria-hidden="true">print</mat-icon>
          PRINT RECEIPT
        </button>
      </div>
    </div>

  </div>
</div>
